import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  SecurityContext,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';

import { of } from 'rxjs';

import { Helpers } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  mapApplyErrorsToIssues,
  SKILLS_IMPORT_INVALID_CODE,
  SkillsImportDataFacade,
} from '@core/tellent-skills-logic';
import type {
  ImportSkillsApplyResult,
  SkillsImport,
  SkillsImportApplyErrorResponse,
  SkillsImportMessage,
  SkillsImportMessageType,
  SkillsImportProficiency,
  SkillsImportResult,
  SkillsImportSkill,
} from '@core/tellent-skills-types';
import { ImportedSkillCardComponent } from '@core/tellent-skills-ui';
import { StepperComponent, StepperStep } from '@core/tellent-ui';
import {
  AiLogoComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  type IconName,
  type IconToken,
  NotificationsController,
  RadioComponent,
  SpinnerComponent,
} from '@core/user-interface-legacy';

type MessageIconConfig = { name: IconName; colorToken: IconToken };
type SanitizedMessage = SkillsImportMessage & { sanitizedText: string };

const MESSAGE_ICON_CONFIG: Record<SkillsImportMessageType, MessageIconConfig> = {
  positive: { name: 'check', colorToken: 'colorIconSuccessDefault' },
  info: { name: 'info-outline', colorToken: 'colorIconInfoDefault' },
  warning: { name: 'warning', colorToken: 'colorIconWarningDefault' },
  blocking_error: { name: 'error', colorToken: 'colorIconCriticalDefault' },
};

@Component({
  selector: 'tlc-import-skills-summary-dialog',
  templateUrl: './import-skills-summary-dialog.component.html',
  styleUrl: './import-skills-summary-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AiLogoComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    GroupComponent,
    IconComponent,
    ImportedSkillCardComponent,
    InfoboxComponent,
    RadioComponent,
    SpinnerComponent,
    StepperComponent,
    TranslatePipe,
    HeadingComponent,
  ],
})
export class ImportSkillsSummaryDialogComponent implements DialogComponentI<
  { id: number },
  { applyResult: ImportSkillsApplyResult }
> {
  private readonly t = injectTranslate();
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(DialogRef);
  private readonly skillsImportDataFacade = inject(SkillsImportDataFacade);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly notificationsController = inject(NotificationsController);
  private readonly dialogService = inject(DialogService);

  private readonly cardElements = viewChildren(ImportedSkillCardComponent, { read: ElementRef });

  protected readonly skillsImport = signal<SkillsImport | null>(null);
  protected readonly addNewPositions = signal<boolean>(true);
  protected readonly isApplying = signal<boolean>(false);
  protected readonly skillDecisions = signal<Array<'create-or-update' | 'skip'>>([]);
  protected readonly newPositions = computed(() =>
    (this.skillsImport()?.result?.positions ?? []).filter(p => p.existingPositionGuid === null),
  );
  protected readonly skillsCount = computed(() => this.skillsImport()?.result?.skillsCount ?? 0);
  protected readonly positionsCount = computed(
    () => this.skillsImport()?.result?.positionsCount ?? 0,
  );
  protected readonly skills = computed(() => this.skillsImport()?.result?.skills ?? []);
  protected readonly groups = computed(() => this.skillsImport()?.result?.groups ?? []);
  protected readonly positions = computed(() => this.skillsImport()?.result?.positions ?? []);
  protected readonly messages = computed(() => this.skillsImport()?.result?.messages ?? []);
  protected readonly sanitizedMessages = computed<SanitizedMessage[]>(() =>
    this.messages().map(message => ({
      ...message,
      sanitizedText: this.sanitizer.sanitize(SecurityContext.HTML, message.text) ?? '',
    })),
  );
  protected readonly positiveAndInfoMessages = computed(() =>
    this.sanitizedMessages().filter(
      message => message.type === 'positive' || message.type === 'info',
    ),
  );
  protected readonly warningMessages = computed(() =>
    this.sanitizedMessages().filter(message => message.type === 'warning'),
  );
  protected readonly blockingErrorMessages = computed(() =>
    this.messages().filter(m => m.type === 'blocking_error'),
  );
  protected readonly messageIconConfig = MESSAGE_ICON_CONFIG;

  protected readonly steps: StepperStep[] = [
    {
      id: 'upload_file',
      label: this.t('tlc.positions.import_skills_flow.stepper.upload_file'),
      active: false,
      completed: true,
    },
    {
      id: 'preview_skills',
      label: this.t('tlc.positions.import_skills_flow.stepper.preview_skills'),
      active: true,
      completed: false,
    },
    {
      id: 'apply_changes',
      label: this.t('tlc.positions.import_skills_flow.stepper.apply_changes'),
      active: false,
      completed: false,
    },
  ];

  public setDialogData(data: { id: number }, _triggerUpdate: boolean): void {
    this.skillsImportDataFacade
      .fetchImport(data.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: skillsImport => {
          this.skillsImport.set(skillsImport);
          this.enableCloseGuard();
        },
        error: () =>
          this.notificationsController.error({ title: this.t('tlc.common.errors.unknown_error') }),
      });
  }

  public applyImport(): void {
    const skillsImport = this.skillsImport();
    if (!skillsImport?.result) return;

    const decisions = this.skillDecisions();
    const skills = skillsImport.result.skills.filter((_, i) => decisions[i] !== 'skip');

    const positions = this.addNewPositions()
      ? skillsImport.result.positions
      : skillsImport.result.positions.filter(p => p.existingPositionGuid !== null);

    const result = { ...skillsImport.result, skills, positions };

    this.lockDialog();
    this.isApplying.set(true);
    this.skillsImportDataFacade
      .applyImport(skillsImport.id, result)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.disableCloseGuard();
          this.dialogRef.close({
            applyResult: {
              success: true,
              skillsCount: data.result?.skillsCount ?? 0,
              positionsCount: data.result?.positionsCount ?? 0,
            },
          });
        },
        error: (err: unknown) => {
          this.dialogRef.closeImmediate({
            applyResult: { success: false, issues: this.extractIssues(err, result) },
          });
        },
      });
  }

  public onImportDecisionChange(index: number, decision: 'create-or-update' | 'skip'): void {
    this.skillDecisions.update(decisions => {
      const updated = [...decisions];
      updated[index] = decision;
      return updated;
    });
  }

  public onSkillSave(
    index: number,
    data: Pick<SkillsImportSkill, 'group' | 'name' | 'description'>,
  ): void {
    this.skillsImport.update(current => {
      if (!current?.result) return current;
      const skills = [...current.result.skills];
      skills[index] = { ...skills[index], ...data };

      return { ...current, result: { ...current.result, skills } };
    });
  }

  public onCardCollapsed(index: number): void {
    setTimeout(() => {
      const next = this.cardElements()[index + 1];
      next?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  public onProficiencyDelete(skillIndex: number, proficiencyIndex: number): void {
    this.skillsImport.update(current => {
      if (!current?.result) return current;
      const skills = [...current.result.skills];
      const proficiencies = skills[skillIndex].proficiencies.filter(
        (_, i) => i !== proficiencyIndex,
      );
      skills[skillIndex] = { ...skills[skillIndex], proficiencies };
      return { ...current, result: { ...current.result, skills } };
    });
  }

  public onProficiencySave(
    skillIndex: number,
    event: {
      index: number;
      data: Pick<SkillsImportProficiency, 'name' | 'description' | 'positions'>;
    },
  ): void {
    this.skillsImport.update(current => {
      if (!current?.result) return current;
      const skills = [...current.result.skills];
      const proficiencies = [...skills[skillIndex].proficiencies];
      proficiencies[event.index] = { ...proficiencies[event.index], ...event.data };
      skills[skillIndex] = { ...skills[skillIndex], proficiencies };

      return { ...current, result: { ...current.result, skills } };
    });
  }

  private extractIssues(err: unknown, submitted: SkillsImportResult): string[] {
    const node = this.findValidationNode(Helpers.camelizeKeys(err));
    return node ? mapApplyErrorsToIssues(node.errors, submitted, this.t) : [];
  }

  private findValidationNode(value: unknown): SkillsImportApplyErrorResponse['error'] | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const record = <Record<string, unknown>>value;
    if (record['code'] === SKILLS_IMPORT_INVALID_CODE && Array.isArray(record['errors'])) {
      return <SkillsImportApplyErrorResponse['error']>value;
    }
    for (const child of Object.values(record)) {
      const found = this.findValidationNode(child);
      if (found) {
        return found;
      }
    }
    return null;
  }

  private disableCloseGuard(): void {
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      closeGuard: () => of(true),
    };
  }

  private enableCloseGuard(): void {
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      closeGuard: () =>
        this.dialogService.error({
          title: this.t('tlc.positions.import_skills_flow.summary_dialog.leave_confirmation.title'),
          message: this.t(
            'tlc.positions.import_skills_flow.summary_dialog.leave_confirmation.message',
          ),
          agreeLabel: this.t(
            'tlc.positions.import_skills_flow.summary_dialog.leave_confirmation.confirm',
          ),
          isCancelable: true,
        }),
    };
    this.dialogRef.containerInstance.detectChanges();
  }

  private lockDialog(): void {
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      disableCloseOnBackdrop: true,
      disableClose: true,
      showCloseButton: false,
    };
    this.dialogRef.containerInstance.dialogCloseVisible = false;
    this.dialogRef.containerInstance.detectChanges();
  }
}
